import React from 'react';
import { AlertTriangle, FileX, Scale, Users } from 'lucide-react';
import { Card } from './ui/card';

const Problem = () => {
  const problems = [
    {
      icon: Users,
      title: "No Clear Agreements",
      description: "People appear in videos, streams, and social media content without formal agreements or documentation."
    },
    {
      icon: AlertTriangle,
      title: "Payment Disputes",
      description: "Months later, individuals request payment or compensation with no clear system to resolve claims."
    },
    {
      icon: FileX,
      title: "Content Removal Demands",
      description: "Creators face demands to remove content, losing valuable work and potentially damaging their channel."
    },
    {
      icon: Scale,
      title: "Legal Risk",
      description: "Without proper documentation, creators face legal risks and reputation damage from appearance disputes."
    }
  ];

  return (
    <section id="problem" className="relative py-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a2e] via-[#1a0a3e] to-[#0a0a2e]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-pink-500/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <span className="px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-400 text-sm font-medium inline-block">
            The Challenge
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            The Hidden Risk in
            <span className="bg-gradient-to-r from-pink-400 to-red-400 bg-clip-text text-transparent"> Content Creation</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Every day, creators face unexpected disputes from people who appeared in their content. 
            There's currently no structured system to prevent these issues.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <Card 
                key={index}
                className="group relative bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-gray-700/50 hover:border-pink-500/50 backdrop-blur-sm p-6 transition-all duration-300 hover:shadow-xl hover:shadow-pink-500/20 hover:-translate-y-2"
              >
                {/* Icon Container */}
                <div className="mb-4 relative">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500/20 to-red-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-pink-400" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500/20 rounded-full blur-xl group-hover:bg-red-500/40 transition-all"></div>
                </div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-pink-400 transition-colors">
                  {problem.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {problem.description}
                </p>

                {/* Decorative Corner */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-pink-500/10 to-transparent rounded-bl-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Card>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className="mt-20 grid md:grid-cols-3 gap-8">
          <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-red-500/10 to-pink-500/10 border border-red-500/20 backdrop-blur-sm">
            <p className="text-5xl font-bold text-red-400 mb-2">73%</p>
            <p className="text-gray-300">of creators face appearance disputes</p>
          </div>
          <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-orange-500/10 to-pink-500/10 border border-orange-500/20 backdrop-blur-sm">
            <p className="text-5xl font-bold text-orange-400 mb-2">£1000s</p>
            <p className="text-gray-300">average cost per dispute resolution</p>
          </div>
          <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/20 backdrop-blur-sm">
            <p className="text-5xl font-bold text-pink-400 mb-2">0</p>
            <p className="text-gray-300">structured solutions currently exist</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Problem;
